import {useEffect, useState} from "react";
import {useAuth} from "@/context/use-auth";
import {
    createEmailConfig,
    deleteEmailConfig,
    getEmailConfig,
    testDraftEmailConfig,
    updateEmailConfig,
    EmailConfigResponse,
    CreateEmailConfigRequest,
    UpdateEmailConfigRequest,
} from "@/services/email-config-services";
import {EMAIL_CONFIG_CHANGED_EVENT} from "@/lib/email-config-events";
import {Button} from "@/components/ui/button";
import {useToast} from "@/hooks/use-toast";
import {Check, Pencil, Send, Trash2} from "lucide-react";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {useAdminTranslation} from "@/i18n/hooks";
import {Text} from "@/components/tailwind/text";
import {Field, Label} from "@/components/tailwind/fieldset";
import {Input} from "@/components/tailwind/input";
import {Select} from "@/components/tailwind/select";
import {Switch, SwitchField} from "@/components/tailwind/switch";
import log from "@/lib/logger";

type ProviderType = "POSTAL_API" | "SMTP";

export default function SectionEmailConfig() {
    const {token} = useAuth();
    const {toast} = useToast();
    const {t} = useAdminTranslation();

    const [config, setConfig] = useState<EmailConfigResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [isDraftTestPassed, setIsDraftTestPassed] = useState(false);

    const [senderEmail, setSenderEmail] = useState("");
    const [senderName, setSenderName] = useState("");
    const [providerType, setProviderType] = useState<ProviderType>("POSTAL_API");
    const [apiUrl, setApiUrl] = useState("");
    const [apiKey, setApiKey] = useState("");
    const [smtpHost, setSmtpHost] = useState("");
    const [smtpPort, setSmtpPort] = useState("587");
    const [smtpAuthentication, setSmtpAuthentication] = useState(true);
    const [smtpUsername, setSmtpUsername] = useState("");
    const [smtpPassword, setSmtpPassword] = useState("");
    const [smtpEncryption, setSmtpEncryption] = useState("STARTTLS");

    function notifyEmailConfigChanged() {
        window.dispatchEvent(new CustomEvent(EMAIL_CONFIG_CHANGED_EVENT));
    }

    useEffect(() => {
        loadData();
    }, [token]);

    async function loadData() {
        setIsLoading(true);
        try {
            const data = await getEmailConfig(token);
            if (data) {
                setConfig(data);
            }
        } catch (error) {
            log.error("Error fetching email config:", error);
        } finally {
            setIsLoading(false);
        }
    }

    function startEditing() {
        if (config) {
            setSenderEmail(config.senderEmail);
            setSenderName(config.senderName);
            setProviderType(config.providerType as ProviderType);
            setApiUrl(config.providerSettingsSummary.apiUrl ?? "");
            setApiKey("");
            setSmtpHost(config.providerSettingsSummary.host ?? "");
            setSmtpPort(config.providerSettingsSummary.port?.toString() ?? "587");
            setSmtpAuthentication(config.providerSettingsSummary.authenticationEnabled ?? true);
            setSmtpUsername("");
            setSmtpPassword("");
            setSmtpEncryption(config.providerSettingsSummary.encryption ?? "STARTTLS");
        } else {
            setSenderEmail("");
            setSenderName("");
            setProviderType("POSTAL_API");
            setApiUrl("");
            setApiKey("");
            setSmtpHost("");
            setSmtpPort("587");
            setSmtpAuthentication(true);
            setSmtpUsername("");
            setSmtpPassword("");
            setSmtpEncryption("STARTTLS");
        }
        setIsDraftTestPassed(false);
        setIsEditing(true);
    }

    function cancelEditing() {
        setIsEditing(false);
        setIsDraftTestPassed(false);
    }

    function handleFormChange<T>(setter: (value: T) => void) {
        return (value: T) => {
            setter(value);
            setIsDraftTestPassed(false);
        };
    }

    async function deleteConfig() {
        if (!config) return;
        if (!window.confirm(t("adminSettings.emailConfig.confirmDelete"))) return;
        setIsDeleting(true);
        try {
            await deleteEmailConfig(token);
            setConfig(null);
            setIsEditing(false);
            toast({
                title: t("adminSettings.emailConfig.toast.deleteSuccess.title"),
            });
            notifyEmailConfigChanged();
        } catch (error) {
            log.error("Error deleting email config:", error);
            toast({
                title: t("adminSettings.emailConfig.toast.deleteError.title"),
                variant: "destructive",
            });
        } finally {
            setIsDeleting(false);
        }
    }

    async function sendDraftTestEmail() {
        if (!isFormValid()) return;
        setIsTesting(true);
        try {
            const result = await testDraftEmailConfig(token, {
                senderEmail: senderEmail.trim(),
                senderName: senderName.trim(),
                providerType,
                providerSettings: buildProviderSettings(),
            });
            if (result && result.success) {
                setIsDraftTestPassed(true);
                toast({
                    title: t("adminSettings.emailConfig.toast.testSuccess.title"),
                    description: t("adminSettings.emailConfig.toast.testSuccess.description", {email: result.recipient}),
                });
            } else {
                setIsDraftTestPassed(false);
                toast({
                    title: t("adminSettings.emailConfig.toast.testError.title"),
                    description: result?.errorSummary ?? t("adminSettings.emailConfig.toast.testError.description"),
                    variant: "destructive",
                });
            }
        } catch (error) {
            log.error("Error testing draft email config:", error);
            setIsDraftTestPassed(false);
            toast({
                title: t("adminSettings.emailConfig.toast.testError.title"),
                description: t("adminSettings.emailConfig.toast.testError.description"),
                variant: "destructive",
            });
        } finally {
            setIsTesting(false);
        }
    }

    function isFormValid(): boolean {
        if (!senderEmail.trim() || !senderName.trim()) return false;
        if (providerType === "POSTAL_API") {
            if (!apiUrl.trim()) return false;
            if (!config && !apiKey.trim()) return false;
        } else if (providerType === "SMTP") {
            if (!smtpHost.trim()) return false;
            if (smtpAuthentication && !config && (!smtpUsername.trim() || !smtpPassword.trim())) return false;
        }
        return true;
    }

    function buildProviderSettings(): Record<string, unknown> {
        if (providerType === "POSTAL_API") {
            return {
                apiUrl: apiUrl.trim(),
                ...(apiKey.trim() ? {apiKey: apiKey.trim()} : {}),
            };
        }
        return {
            host: smtpHost.trim(),
            port: parseInt(smtpPort) || 587,
            authentication: smtpAuthentication,
            ...(smtpAuthentication && smtpUsername.trim() ? {username: smtpUsername.trim()} : {}),
            ...(smtpAuthentication && smtpPassword.trim() ? {password: smtpPassword.trim()} : {}),
            encryption: smtpEncryption,
        };
    }

    async function save() {
        if (!isFormValid()) return;
        setIsSaving(true);
        try {
            if (config) {
                const request: UpdateEmailConfigRequest = {
                    senderEmail: senderEmail.trim(),
                    senderName: senderName.trim(),
                    providerType,
                    providerSettings: buildProviderSettings(),
                    enabled: true,
                };
                await updateEmailConfig(token, request);
            } else {
                const request: CreateEmailConfigRequest = {
                    senderEmail: senderEmail.trim(),
                    senderName: senderName.trim(),
                    providerType,
                    providerSettings: buildProviderSettings(),
                };
                await createEmailConfig(token, request);
            }
            toast({
                title: t("adminSettings.emailConfig.toast.saveSuccess.title"),
                description: t("adminSettings.emailConfig.toast.saveSuccess.description"),
            });
            setIsEditing(false);
            await loadData();
            notifyEmailConfigChanged();
        } catch (error) {
            log.error("Error saving email config:", error);
            toast({
                title: t("adminSettings.emailConfig.toast.saveError.title"),
                description: t("adminSettings.emailConfig.toast.saveError.description"),
                variant: "destructive",
            });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="text-xl font-bold">
                        {t("adminSettings.emailConfig.title")}
                    </CardTitle>

                    {!isEditing && config && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={startEditing}
                                className="flex items-center"
                                aria-label={t("adminSettings.emailConfig.button.edit.ariaLabel")}
                                data-testid="edit-email-config-button"
                            >
                                <Pencil className="h-4 w-4 mr-1"/>
                                {t("adminSettings.emailConfig.button.edit.text")}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={deleteConfig}
                                disabled={isDeleting}
                                className="flex items-center text-red-600 dark:text-red-400"
                                data-testid="delete-email-config-button"
                            >
                                <Trash2 className="h-4 w-4 mr-1"/>
                                {isDeleting
                                    ? t("adminSettings.emailConfig.button.delete.deleting")
                                    : t("adminSettings.emailConfig.button.delete.text")}
                            </Button>
                        </div>
                    )}
                </div>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t("adminSettings.emailConfig.loading")}
                    </div>
                ) : isEditing ? (
                    <div className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <Field>
                                <Label>{t("adminSettings.emailConfig.fields.senderEmail")}</Label>
                                <Input
                                    type="email"
                                    value={senderEmail}
                                    onChange={(e) => handleFormChange(setSenderEmail)(e.target.value)}
                                    data-testid="sender-email-input"
                                />
                            </Field>
                            <Field>
                                <Label>{t("adminSettings.emailConfig.fields.senderName")}</Label>
                                <Input
                                    value={senderName}
                                    onChange={(e) => handleFormChange(setSenderName)(e.target.value)}
                                    data-testid="sender-name-input"
                                />
                            </Field>
                        </div>

                        <Field>
                            <Label>{t("adminSettings.emailConfig.fields.providerType")}</Label>
                            <Select
                                value={providerType}
                                onChange={(e) => handleFormChange(setProviderType)(e.target.value as ProviderType)}
                                data-testid="provider-type-select"
                            >
                                <option value="POSTAL_API">{t("adminSettings.emailConfig.provider.POSTAL_API")}</option>
                                <option value="SMTP">{t("adminSettings.emailConfig.provider.SMTP")}</option>
                            </Select>
                        </Field>

                        {providerType === "POSTAL_API" && (
                            <div className="grid gap-4 md:grid-cols-2">
                                <Field>
                                    <Label>{t("adminSettings.emailConfig.fields.apiUrl")}</Label>
                                    <Input
                                        value={apiUrl}
                                        onChange={(e) => handleFormChange(setApiUrl)(e.target.value)}
                                        data-testid="api-url-input"
                                    />
                                </Field>
                                <Field>
                                    <Label>{t("adminSettings.emailConfig.fields.apiKey")}</Label>
                                    <Input
                                        type="password"
                                        value={apiKey}
                                        onChange={(e) => handleFormChange(setApiKey)(e.target.value)}
                                        placeholder={config?.providerSettingsSummary.apiKeyConfigured
                                            ? t("adminSettings.emailConfig.hints.apiKeyHidden")
                                            : undefined}
                                        data-testid="api-key-input"
                                    />
                                    {config?.providerSettingsSummary.apiKeyConfigured && (
                                        <Text className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                            {t("adminSettings.emailConfig.hints.newApiKey")}
                                        </Text>
                                    )}
                                </Field>
                            </div>
                        )}

                        {providerType === "SMTP" && (
                            <>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <Field>
                                        <Label>{t("adminSettings.emailConfig.fields.smtpHost")}</Label>
                                        <Input
                                            value={smtpHost}
                                            onChange={(e) => handleFormChange(setSmtpHost)(e.target.value)}
                                            placeholder="smtp.example.com"
                                            data-testid="smtp-host-input"
                                        />
                                    </Field>
                                    <Field>
                                        <Label>{t("adminSettings.emailConfig.fields.smtpPort")}</Label>
                                        <Input
                                            type="number"
                                            value={smtpPort}
                                            onChange={(e) => handleFormChange(setSmtpPort)(e.target.value)}
                                            data-testid="smtp-port-input"
                                        />
                                    </Field>
                                </div>
                                <SwitchField>
                                    <Label>{t("adminSettings.emailConfig.fields.smtpAuthentication")}</Label>
                                    <Switch
                                        checked={smtpAuthentication}
                                        onChange={handleFormChange(setSmtpAuthentication)}
                                        color="blue"
                                        data-testid="smtp-authentication-switch"
                                    />
                                </SwitchField>
                                {smtpAuthentication && (
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <Field>
                                            <Label>{t("adminSettings.emailConfig.fields.smtpUsername")}</Label>
                                            <Input
                                                value={smtpUsername}
                                                onChange={(e) => handleFormChange(setSmtpUsername)(e.target.value)}
                                                placeholder={config?.providerSettingsSummary.usernameConfigured
                                                    ? t("adminSettings.emailConfig.hints.credentialHidden")
                                                    : undefined}
                                                data-testid="smtp-username-input"
                                            />
                                        </Field>
                                        <Field>
                                            <Label>{t("adminSettings.emailConfig.fields.smtpPassword")}</Label>
                                            <Input
                                                type="password"
                                                value={smtpPassword}
                                                onChange={(e) => handleFormChange(setSmtpPassword)(e.target.value)}
                                                placeholder={config?.providerSettingsSummary.passwordConfigured
                                                    ? t("adminSettings.emailConfig.hints.credentialHidden")
                                                    : undefined}
                                                data-testid="smtp-password-input"
                                            />
                                        </Field>
                                    </div>
                                )}
                                <Field>
                                    <Label>{t("adminSettings.emailConfig.fields.smtpEncryption")}</Label>
                                    <Select
                                        value={smtpEncryption}
                                        onChange={(e) => handleFormChange(setSmtpEncryption)(e.target.value)}
                                        data-testid="smtp-encryption-select"
                                    >
                                        <option value="STARTTLS">STARTTLS</option>
                                        <option value="SSL_TLS">SSL/TLS</option>
                                        <option value="NONE">{t("adminSettings.emailConfig.encryption.none")}</option>
                                    </Select>
                                </Field>
                            </>
                        )}

                        <div className="flex space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={sendDraftTestEmail}
                                disabled={isTesting || isSaving || !isFormValid()}
                                className="flex items-center"
                                data-testid="test-draft-email-config-button"
                            >
                                <Send className="h-4 w-4 mr-1"/>
                                {isTesting
                                    ? t("adminSettings.emailConfig.button.test.sending")
                                    : t("adminSettings.emailConfig.button.test.text")}
                            </Button>
                            <Button
                                size="sm"
                                onClick={save}
                                disabled={isSaving || !isDraftTestPassed}
                                className="flex items-center"
                                data-testid="save-email-config-button"
                            >
                                <Check className="h-4 w-4 mr-1"/>
                                {isSaving
                                    ? t("adminSettings.emailConfig.button.save.saving")
                                    : t("adminSettings.emailConfig.button.save.save")}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={cancelEditing}
                                disabled={isSaving}
                            >
                                {t("adminSettings.emailConfig.button.cancel")}
                            </Button>
                        </div>
                    </div>
                ) : config ? (
                    <div className="space-y-3">
                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <Text className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("adminSettings.emailConfig.fields.senderEmail")}
                                </Text>
                                <Text className="text-sm font-medium" data-testid="display-sender-email">
                                    {config.senderEmail}
                                </Text>
                            </div>
                            <div>
                                <Text className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("adminSettings.emailConfig.fields.senderName")}
                                </Text>
                                <Text className="text-sm font-medium" data-testid="display-sender-name">
                                    {config.senderName}
                                </Text>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <Text className="text-xs text-gray-500 dark:text-gray-400">
                                    {t("adminSettings.emailConfig.fields.providerType")}
                                </Text>
                                <Text className="text-sm font-medium" data-testid="display-provider-type">
                                    {t(`adminSettings.emailConfig.provider.${config.providerType}`)}
                                </Text>
                            </div>
                            {config.providerType === "POSTAL_API" && (
                                <div>
                                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                                        {t("adminSettings.emailConfig.fields.apiUrl")}
                                    </Text>
                                    <Text className="text-sm font-medium" data-testid="display-api-url">
                                        {config.providerSettingsSummary.apiUrl}
                                    </Text>
                                </div>
                            )}
                            {config.providerType === "SMTP" && (
                                <div>
                                    <Text className="text-xs text-gray-500 dark:text-gray-400">
                                        {t("adminSettings.emailConfig.fields.smtpHost")}
                                    </Text>
                                    <Text className="text-sm font-medium" data-testid="display-smtp-host">
                                        {config.providerSettingsSummary.host}
                                        {config.providerSettingsSummary.port && `:${config.providerSettingsSummary.port}`}
                                    </Text>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <Text className="text-sm text-gray-500 dark:text-gray-400">
                            {t("adminSettings.emailConfig.notConfigured")}
                        </Text>
                        <Button
                            size="sm"
                            onClick={startEditing}
                            data-testid="configure-email-button"
                        >
                            {t("adminSettings.emailConfig.button.configure")}
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
